
export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || 'G-W3S5MZMPNH';
export const JOURNEY_TRACKING_ENABLED = process.env.NEXT_PUBLIC_GA4_JOURNEY_TRACKING_ENABLED === 'true';
export const JOURNEY_TRACKING_DEBUG = process.env.NEXT_PUBLIC_GA4_JOURNEY_TRACKING_DEBUG === 'true';
export const JOURNEY_BACKEND_PERSISTENCE_ENABLED = JOURNEY_TRACKING_ENABLED;

const JOURNEY_STATE_KEY = 'mynawoo_ga4_journey';
const JOURNEY_IDENTITY_KEY = 'mynawoo_ga4_identity';
const JOURNEY_TIMEOUT_MS = 30 * 60 * 1000;
const JOURNEY_OUTBOX_KEY = 'mynawoo_journey_outbox';
let journeyBackendToken: string | null = null;
let flushingJourneyOutbox = false;
let journeyFlushTimer: ReturnType<typeof setTimeout> | null = null;
let journeyRetryDelay = 5000;
let journeyListenersInstalled = false;

// FetchData (encryption, CSRF) loads only when a signed-in learner's events are sent,
// so it stays out of the public pages' main bundle.
async function postJourneyEvents(token: string, events: StoredJourneyEvent[]) {
  const { requestData } = await import('@/components/FetchData');
  return requestData('api/student/journey/', token, 'POST', {
    action: 'track_events',
    events: events.map(event => ({
      action: 'track_event',
      event_id: event.params.event_id,
      name: event.name,
      course_code: event.params.course_code || '',
      lesson_code: event.params.lesson_code || event.params.from_lesson_code || '',
      session_id: event.params.journey_session_id || '',
      metadata: event.params,
    })),
  }, undefined, { keepalive: true });
}

function scheduleJourneyFlush(delay = journeyRetryDelay) {
  if (journeyFlushTimer !== null || !journeyBackendToken) return;
  journeyFlushTimer = setTimeout(() => {
    journeyFlushTimer = null;
    void flushJourneyOutbox();
  }, delay);
}

type JourneyState = {
  userId: string;
  journeySessionId: string;
  tabId: string;
  sequence: number;
  lastActivityAt: number;
};

declare global {
  interface Window {
    __MYNAWOO_ANALYTICS_DEBUG__?: Array<{ name: string; params: Record<string, unknown> }>;
  }
}

function randomId() {
  try { return crypto.randomUUID(); } catch {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const value = Math.floor(Math.random() * 16);
      return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    });
  }
}

function readJourneyState(): JourneyState | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(JOURNEY_STATE_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
}

function writeJourneyState(state: JourneyState) {
  try { sessionStorage.setItem(JOURNEY_STATE_KEY, JSON.stringify(state)); } catch {  }
}

function clearAccountScopedState() {
  try {
    sessionStorage.removeItem(JOURNEY_STATE_KEY);
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith('mynawoo_activation:')) sessionStorage.removeItem(key);
    }
    sessionStorage.removeItem('mynawoo_activation_session');
    localStorage.removeItem(JOURNEY_OUTBOX_KEY);
  } catch {  }
}

type StoredJourneyEvent = { name: string; params: Record<string, unknown> };

function readJourneyOutbox(): StoredJourneyEvent[] {
  try {
    const value = JSON.parse(localStorage.getItem(JOURNEY_OUTBOX_KEY) || '[]');
    return Array.isArray(value) ? value.slice(-500) : [];
  } catch { return []; }
}

function writeJourneyOutbox(events: StoredJourneyEvent[]) {
  try { localStorage.setItem(JOURNEY_OUTBOX_KEY, JSON.stringify(events.slice(-500))); } catch {  }
}

async function flushJourneyOutbox() {
  if (!journeyBackendToken || flushingJourneyOutbox) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const batch: StoredJourneyEvent[] = [];
  let bytes = 0;
  for (const event of readJourneyOutbox().slice(0, 20)) {
    const size = new TextEncoder().encode(JSON.stringify(event)).length + 512;
    if (batch.length && bytes + size > 40000) break;
    batch.push(event);
    bytes += size;
  }
  if (!batch.length) return;
  const token = journeyBackendToken;
  flushingJourneyOutbox = true;
  try {
      const response = await postJourneyEvents(token, batch);
      if (!response?.recorded || !Array.isArray(response.event_ids)) throw new Error('Unacknowledged journey batch');
      if (token !== journeyBackendToken) return;
      const acknowledged = new Set(response.event_ids);
      writeJourneyOutbox(readJourneyOutbox().filter(item => !acknowledged.has(item.params.event_id)));
      journeyRetryDelay = 5000;
  } catch {
    journeyRetryDelay = Math.min(journeyRetryDelay * 2, 60000);
  } finally {
    flushingJourneyOutbox = false;
    if (readJourneyOutbox().length) scheduleJourneyFlush();
  }
}

export function setJourneyBackendToken(token?: string | null) {
  journeyBackendToken = token || null;
  if (!journeyBackendToken && journeyFlushTimer !== null) {
    clearTimeout(journeyFlushTimer);
    journeyFlushTimer = null;
  }
  if (typeof window !== 'undefined' && !journeyListenersInstalled) {
    journeyListenersInstalled = true;
    window.addEventListener('online', () => { journeyRetryDelay = 5000; scheduleJourneyFlush(0); });
    window.addEventListener('pagehide', () => { void flushJourneyOutbox(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flushJourneyOutbox();
    });
  }
  if (journeyBackendToken) scheduleJourneyFlush();
}

export function syncJourneyIdentity(userId?: string | null) {
  if (typeof window === 'undefined' || !JOURNEY_TRACKING_ENABLED) return;
  try {
    const previous = sessionStorage.getItem(JOURNEY_IDENTITY_KEY);
    const next = userId || '';
    if (previous !== next) clearAccountScopedState();
    if (next) sessionStorage.setItem(JOURNEY_IDENTITY_KEY, next);
    else sessionStorage.removeItem(JOURNEY_IDENTITY_KEY);
  } catch {  }
}

function nextJourneyEnvelope(userId: string) {
  const now = Date.now();
  let state = readJourneyState();
  if (!state || state.userId !== userId || now - state.lastActivityAt > JOURNEY_TIMEOUT_MS) {
    state = {
      userId,
      journeySessionId: randomId(),
      tabId: randomId(),
      sequence: 0,
      lastActivityAt: now,
    };
  }
  state.sequence += 1;
  state.lastActivityAt = now;
  writeJourneyState(state);
  return {
    event_id: randomId(),
    timestamp: new Date(now).toISOString(),
    journey_session_id: state.journeySessionId,
    tab_id: state.tabId,
    event_sequence: state.sequence,
  };
}

export function newJourneyVisitId() {
  return randomId();
}

export type JourneyCheckoutContext = {
  journey_session_id?: string;
  tab_id?: string;
  ga_client_id?: string;
  ga_session_id?: string;
};

function getGaField(fieldName: 'client_id' | 'session_id', timeoutMs = 250): Promise<string | undefined> {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'development' || !window.gtag) {
    return Promise.resolve(undefined);
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value?: unknown) => {
      if (settled) return;
      settled = true;
      resolve(value === undefined || value === null ? undefined : String(value).slice(0, 100));
    };
    const timer = window.setTimeout(() => finish(), timeoutMs);
    try {
      window.gtag?.('get', GA_MEASUREMENT_ID, fieldName, (value: unknown) => {
        window.clearTimeout(timer);
        finish(value);
      });
    } catch {
      window.clearTimeout(timer);
      finish();
    }
  });
}

export async function getJourneyCheckoutContext(userId?: string | null): Promise<JourneyCheckoutContext> {
  if (typeof window === 'undefined' || !JOURNEY_TRACKING_ENABLED || !userId) return {};
  try {
    syncJourneyIdentity(userId);
    const state = readJourneyState();
    const [gaClientId, gaSessionId] = await Promise.all([
      getGaField('client_id'),
      getGaField('session_id'),
    ]);
    return {
      journey_session_id: state?.userId === userId ? state.journeySessionId : undefined,
      tab_id: state?.userId === userId ? state.tabId : undefined,
      ga_client_id: gaClientId,
      ga_session_id: gaSessionId,
    };
  } catch {
    return {};
  }
}

export function sendGaEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'development') return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () { window.dataLayer?.push(arguments); };
    }
    window.gtag('event', name, params);
  } catch {
  }
}

export function setGaUserId(userId?: string | null) {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'development') return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () { window.dataLayer?.push(arguments); };
    }
    window.gtag('config', GA_MEASUREMENT_ID, { user_id: userId || null });
  } catch {
  }
}

export function sendJourneyEvent(
  name: string,
  userId: string | null | undefined,
  params: Record<string, unknown> = {},
) {
  if (typeof window === 'undefined' || !JOURNEY_TRACKING_ENABLED || !userId) return false;
  try {
    syncJourneyIdentity(userId);
    const payload = Object.fromEntries(
      Object.entries({ ...nextJourneyEnvelope(userId), ...params })
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 100) : value]),
    );
    if (Object.keys(payload).length > 25) return false;
    if (process.env.NODE_ENV === 'development') {
      if (JOURNEY_TRACKING_DEBUG) {
        window.__MYNAWOO_ANALYTICS_DEBUG__ = window.__MYNAWOO_ANALYTICS_DEBUG__ || [];
        window.__MYNAWOO_ANALYTICS_DEBUG__.push({ name, params: payload });
      }
      if (name !== 'purchase' && name !== 'subscription_activated') {
        writeJourneyOutbox([...readJourneyOutbox(), { name, params: payload }]);
        scheduleJourneyFlush();
      }
      return true;
    }
    if (name !== 'purchase' && name !== 'subscription_activated') {
      writeJourneyOutbox([...readJourneyOutbox(), { name, params: payload }]);
      scheduleJourneyFlush();
    }
    sendGaEvent(name, payload);
    return true;
  } catch {
    return false;
  }
}
